import type { JSX } from 'react';

import { cn } from '../lib/cn';
import { PILL_SHELL } from '../lib/pill-shell';

import type { OperationalStatus } from './operational-status';
import {
  CALENDAR_DOMAIN_STYLES,
  CONTRACT_DOMAIN_STYLES,
  CUSTOMER_DOMAIN_STYLES,
  type CalendarBadgeStatus,
  type ContractBadgeStatus,
  type CustomerBadgeStatus,
  DELIVERY_NOTE_DOMAIN_STYLES,
  type DeliveryNoteBadgeStatus,
  type DomainColor,
  HOLIDAY_DOMAIN_STYLES,
  type HolidayBadgeStatus,
  INVOICE_DOMAIN_STYLES,
  type InvoiceBadgeStatus,
  MENU_DOMAIN_STYLES,
  type MenuBadgeStatus,
  ORDER_DOMAIN_STYLES,
  type OrderBadgeStatus,
  PRODUCTION_DOMAIN_STYLES,
  type ProductionBadgeStatus,
  USER_DOMAIN_STYLES,
  type UserBadgeStatus,
} from './status-badge-domains';

/**
 * Written out in full, one entry per status, and never assembled from pieces.
 *
 * Tailwind scans source text; it cannot see a class name built at runtime.
 * `bg-status-${status}` looks correct, type checks, and passes a test that
 * inspects the className string — and ships a badge with no background at all,
 * because the rule was never generated. The repetition below is the price of
 * a scanner that reads files rather than running them.
 */
const STATUS_STYLES: Record<OperationalStatus, string> = {
  draft: 'bg-status-draft',
  confirmed: 'bg-status-confirmed',
  'in-progress': 'bg-status-in-progress',
  delivered: 'bg-status-delivered',
  problem: 'bg-status-problem',
};

const DOMAIN_COLOR_STYLES: Record<DomainColor, string> = {
  success: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-600/20',
  warning: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/20',
  danger: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-600/20',
  neutral: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-500/20',
};

/** A small leading dot keeps the state scannable at a glance. */
const DOMAIN_DOT_STYLES: Record<DomainColor, string> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  neutral: 'bg-slate-400',
};

/**
 * `payment-receipts/spec.md` → "Pantalla de Cobranzas en el back-office":
 * the three `Payment.status` values. Kept local to this file, unlike the
 * other domains in `status-badge-domains.ts`, per this change's allowed
 * edit surface.
 */
export type PaymentBadgeStatus = 'RECORDED' | 'CLEARED' | 'VOIDED';

const PAYMENT_DOMAIN_STYLES: Record<PaymentBadgeStatus, { color: DomainColor; label: string }> = {
  RECORDED: { color: 'neutral', label: 'Registrado' },
  CLEARED: { color: 'success', label: 'Imputado' },
  VOIDED: { color: 'danger', label: 'Anulado' },
};

export type {
  CalendarBadgeStatus,
  ContractBadgeStatus,
  CustomerBadgeStatus,
  DeliveryNoteBadgeStatus,
  HolidayBadgeStatus,
  InvoiceBadgeStatus,
  MenuBadgeStatus,
  OrderBadgeStatus,
  ProductionBadgeStatus,
  StatusBadgeDomain,
  UserBadgeStatus,
} from './status-badge-domains';

export type StatusBadgeProps =
  | { status: OperationalStatus; label: string; className?: string }
  | { domain: 'customer'; status: CustomerBadgeStatus; className?: string }
  | { domain: 'contract'; status: ContractBadgeStatus; className?: string }
  | { domain: 'menu'; status: MenuBadgeStatus; className?: string }
  | { domain: 'calendar'; status: CalendarBadgeStatus; className?: string }
  | { domain: 'holiday'; status: HolidayBadgeStatus; className?: string }
  | { domain: 'order'; status: OrderBadgeStatus; className?: string }
  | { domain: 'production'; status: ProductionBadgeStatus; className?: string }
  | { domain: 'deliverynote'; status: DeliveryNoteBadgeStatus; className?: string }
  | { domain: 'invoice'; status: InvoiceBadgeStatus; className?: string }
  | { domain: 'user'; status: UserBadgeStatus; className?: string }
  | { domain: 'payment'; status: PaymentBadgeStatus; className?: string };

/**
 * The label is not optional, on purpose.
 *
 * A badge that says everything with colour says nothing on a kitchen screen
 * seen at an angle, on a phone in direct sunlight, or to the roughly one in
 * twelve men who cannot separate the red from the green.
 */
export function StatusBadge(props: StatusBadgeProps): JSX.Element {
  const { className } = props;
  const { colorClass, dotClass, label } = resolveDomainOrOperational(props);

  if (dotClass === undefined) {
    return <span className={cn(PILL_SHELL, 'text-white', colorClass, className)}>{label}</span>;
  }

  return (
    <span className={cn(PILL_SHELL, colorClass, className)}>
      <span aria-hidden="true" className={cn('size-1.5 rounded-full', dotClass)} />
      {label}
    </span>
  );
}

function resolveDomainOrOperational(props: StatusBadgeProps): {
  colorClass: string;
  dotClass?: string;
  label: string;
} {
  if (!('domain' in props)) {
    return { colorClass: STATUS_STYLES[props.status], label: props.label };
  }

  const entry =
    props.domain === 'customer'
      ? CUSTOMER_DOMAIN_STYLES[props.status]
      : props.domain === 'contract'
        ? CONTRACT_DOMAIN_STYLES[props.status]
        : props.domain === 'menu'
          ? MENU_DOMAIN_STYLES[props.status]
          : props.domain === 'calendar'
            ? CALENDAR_DOMAIN_STYLES[props.status]
            : props.domain === 'holiday'
              ? HOLIDAY_DOMAIN_STYLES[props.status]
              : props.domain === 'order'
                ? ORDER_DOMAIN_STYLES[props.status]
                : props.domain === 'production'
                  ? PRODUCTION_DOMAIN_STYLES[props.status]
                  : props.domain === 'deliverynote'
                    ? DELIVERY_NOTE_DOMAIN_STYLES[props.status]
                    : props.domain === 'invoice'
                      ? INVOICE_DOMAIN_STYLES[props.status]
                      : props.domain === 'user'
                        ? USER_DOMAIN_STYLES[props.status]
                        : PAYMENT_DOMAIN_STYLES[props.status];

  // A status outside the typed union is impossible to type but possible at
  // runtime if the backend adds a value; it falls back to neutral with the
  // raw value as label rather than throwing.
  if (entry === undefined) {
    return {
      colorClass: DOMAIN_COLOR_STYLES.neutral,
      dotClass: DOMAIN_DOT_STYLES.neutral,
      label: props.status,
    };
  }

  return {
    colorClass: DOMAIN_COLOR_STYLES[entry.color],
    dotClass: DOMAIN_DOT_STYLES[entry.color],
    label: entry.label,
  };
}
